/**
 * RUG PDP — small, delegated enhancements for rug product templates.
 * The theme remains responsible for product forms, variant changes, lightbox,
 * x-modal and dynamic checkout. This file only connects the rug-specific UI.
 */
(function () {
  if (window.__rugPdpInitialized) return;
  window.__rugPdpInitialized = true;

  var lastDialogTrigger = null;
  var galleryPointer = null;
  var reviewCheckSequence = 0;

  function openDialog(id, trigger) {
    var dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return;
    lastDialogTrigger = trigger || null;
    if (!dialog.open) dialog.showModal();
  }

  function closeDialog(dialog) {
    if (dialog && dialog.open) dialog.close();
  }

  function openCustomerService(event) {
    var advisorButton = event.target.closest('[data-rug-open-customer-service]');
    if (!advisorButton) return false;

    var trigger = document.querySelector('[data-rug-customer-service-trigger]');
    if (!trigger) return true;

    event.preventDefault();
    var clickable = trigger.closest('button, [role="button"], a') || trigger;
    clickable.click();
    return true;
  }

  document.addEventListener('click', function (event) {
    if (openCustomerService(event)) return;

    var opener = event.target.closest('[data-rug-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.getAttribute('data-rug-dialog-open'), opener);
      return;
    }

    var closer = event.target.closest('[data-rug-dialog-close]');
    if (closer) {
      closeDialog(closer.closest('dialog.rug-dialog'));
      return;
    }

    var openDialogEl = event.target.closest('dialog.rug-dialog');
    if (openDialogEl && event.target === openDialogEl) {
      closeDialog(openDialogEl);
      return;
    }

    var planSetButton = event.target.closest('[data-rug-plan-set]');
    if (planSetButton) {
      var root = planSetButton.closest('[data-rug-plan-root]');
      if (!root) return;
      var index = planSetButton.getAttribute('data-rug-plan-set');
      root.setAttribute('data-rug-plan-size', index);
      root.querySelectorAll('[data-rug-plan-set]').forEach(function (button) {
        button.setAttribute('aria-pressed', button === planSetButton ? 'true' : 'false');
      });
      return;
    }

    var moreButton = event.target.closest('[data-rug-related-more]');
    if (moreButton) revealMoreRelated(moreButton);
  });

  document.addEventListener(
    'close',
    function (event) {
      if (!event.target.matches || !event.target.matches('dialog.rug-dialog')) return;
      if (lastDialogTrigger && typeof lastDialogTrigger.focus === 'function') {
        lastDialogTrigger.focus();
      }
    },
    true
  );

  /* One clean tap on a mobile gallery image opens the theme's existing lightbox.
     Pointer movement is never cancelled, so horizontal swipe and vertical page scroll remain native. */
  document.addEventListener('pointerdown', function (event) {
    if (!window.matchMedia('(max-width: 999px)').matches) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    var media = event.target.closest('.shopify-section--rug-main-product .product-gallery__media');
    if (!media || media.getAttribute('data-media-type') !== 'image') return;
    if (event.target.closest('button, input, select, textarea, video, model-viewer')) return;

    galleryPointer = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      time: Date.now(),
      media: media
    };
  });

  document.addEventListener('pointercancel', function (event) {
    if (galleryPointer && galleryPointer.id === event.pointerId) galleryPointer = null;
  });

  document.addEventListener('pointerup', function (event) {
    if (!galleryPointer || galleryPointer.id !== event.pointerId) return;

    var start = galleryPointer;
    galleryPointer = null;
    if (!window.matchMedia('(max-width: 999px)').matches) return;
    if (event.target.closest('.product-gallery__media') !== start.media) return;

    var dx = event.clientX - start.x;
    var dy = event.clientY - start.y;
    var distance = Math.sqrt(dx * dx + dy * dy);
    var elapsed = Date.now() - start.time;
    if (distance > 10 || elapsed > 650) return;

    var gallery = start.media.closest('product-gallery');
    var lightboxTrigger = gallery && gallery.querySelector('[data-rug-gallery-lightbox-trigger]');
    if (lightboxTrigger) {
      event.preventDefault();
      lightboxTrigger.click();
    }
  });

  /* Legacy product-details rows from the original template, updated without
     polling or monkey-patching history.pushState. */
  function normalizeDetailValue(value, dimension) {
    if (value === null || value === undefined) return '';
    var clean = String(value).trim();
    if (!clean) return '';
    if (dimension && clean.toLowerCase().indexOf('cm') === -1) clean += ' cm';
    return clean;
  }

  function findCurrentVariantId(root, explicitVariant) {
    if (explicitVariant && explicitVariant.id) return String(explicitVariant.id);

    var urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) return String(urlVariant);

    var productForm = root.closest('.rug-buybox') && root.closest('.rug-buybox').querySelector('form[action*="/cart/add"]');
    var variantInput = productForm && productForm.querySelector('[name="id"]');
    return variantInput && variantInput.value ? String(variantInput.value) : '';
  }

  function updateLegacyProductDetails(scope, explicitVariant) {
    (scope || document).querySelectorAll('[data-ad-variant-details-root]').forEach(function (root) {
      var productId = root.getAttribute('data-ad-variant-details-root');
      var dataElement = document.getElementById('ad-variant-details-data-' + productId);
      if (!dataElement) return;

      if (!root.__rugVariantDetails) {
        try {
          root.__rugVariantDetails = JSON.parse(dataElement.textContent || '[]');
        } catch (error) {
          root.__rugVariantDetails = [];
        }
      }

      var variantId = findCurrentVariantId(root, explicitVariant);
      var variant = root.__rugVariantDetails.find(function (item) { return String(item.id) === variantId; });
      if (!variant) return;

      [
        ['height', true],
        ['width', true],
        ['depth', true],
        ['length', true],
        ['diameter', true],
        ['weight', false]
      ].forEach(function (entry) {
        var key = entry[0];
        var item = root.querySelector('[data-ad-variant-detail-item="' + key + '"]');
        var valueElement = root.querySelector('[data-ad-variant-detail-value="' + key + '"]');
        if (!item || !valueElement) return;

        var value = normalizeDetailValue(variant[key], entry[1]);
        valueElement.textContent = value;
        item.hidden = !value;
      });
    });
  }

  /* Variant-dependent specification rows. */
  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (variant) {
      document.querySelectorAll('[data-rug-spec="size"]').forEach(function (el) {
        el.textContent = variant.title || '';
        var row = el.closest('.rug-spec__row');
        if (row) row.hidden = !variant.title;
      });

      document.querySelectorAll('[data-rug-spec="sku"]').forEach(function (el) {
        el.textContent = variant.sku || '';
        var row = el.closest('.rug-spec__row');
        if (row) row.hidden = !variant.sku;
      });
    }

    updateLegacyProductDetails(document, variant);

    // product-rerender may replace the reviews row just after this event.
    window.setTimeout(function () { runTrustedReviewChecks(document); }, 50);
    window.setTimeout(function () { runTrustedReviewChecks(document); }, 650);
    window.setTimeout(function () { initLinkedVariants(document); }, 80);
    window.setTimeout(function () { initLinkedVariants(document); }, 500);
  });

  /* Hide the whole product-review row when the existing eTrusted summary widget
     does not render a real, visible result. Mirrors the check used by the live PDP. */
  function runTrustedReviewChecks(scope) {
    var rows = (scope || document).querySelectorAll('[data-trusted-rating-row]');
    rows.forEach(function (row) {
      var wrapper = row.closest('[data-rug-trusted-row-wrap]');
      if (!wrapper) return;

      reviewCheckSequence += 1;
      var token = String(reviewCheckSequence);
      wrapper.dataset.rugReviewCheckToken = token;
      wrapper.classList.remove('is-ready', 'is-hidden');
      wrapper.classList.add('is-checking');

      function check(attempt) {
        if (wrapper.dataset.rugReviewCheckToken !== token || !wrapper.isConnected) return;
        var widget = row.querySelector('etrusted-widget');
        var hasRenderedWidget = !!widget && widget.offsetWidth > 20 && widget.offsetHeight > 12;

        if (hasRenderedWidget) {
          wrapper.classList.remove('is-checking', 'is-hidden');
          wrapper.classList.add('is-ready');
          return;
        }

        if (attempt >= 10) {
          wrapper.classList.remove('is-checking', 'is-ready');
          wrapper.classList.add('is-hidden');
          return;
        }

        window.setTimeout(function () { check(attempt + 1); }, 350);
      }

      check(0);
    });
  }


  /* The live linked-variants snippet intentionally omits the product currently
     being viewed. On rug PDPs we add it back and mark it with the same strong
     border as a selected variant. This runs after every product rerender. */
  function normalizeProductPath(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return String(url || '').split('?')[0].replace(/\/$/, '');
    }
  }

  function initLinkedVariants(scope) {
    (scope || document).querySelectorAll('[data-rug-linked-variants]').forEach(function (wrapper) {
      var component = wrapper.querySelector('.product-linked-variants');
      var grid = component && component.querySelector('.product-linked-variants__grid');
      if (!grid) return;

      var currentPath = normalizeProductPath(wrapper.getAttribute('data-current-product-url') || window.location.pathname);
      var currentItem = null;

      grid.querySelectorAll('.product-linked-variants__item[href]').forEach(function (item) {
        var isCurrent = normalizeProductPath(item.getAttribute('href')) === currentPath;
        item.classList.toggle('is-current', isCurrent);
        if (isCurrent) {
          item.setAttribute('aria-current', 'page');
          currentItem = item;
        } else {
          item.removeAttribute('aria-current');
        }
      });

      if (!currentItem) {
        var template = wrapper.parentElement && wrapper.parentElement.querySelector('template[data-rug-current-linked-template]');
        if (template && template.content.firstElementChild) {
          currentItem = template.content.firstElementChild.cloneNode(true);
          grid.prepend(currentItem);
        }
      } else if (grid.firstElementChild !== currentItem) {
        grid.prepend(currentItem);
      }
    });
  }

  /* Related products reveal 4/2 at a time, without network requests. */
  function getStep(root) {
    var desktop = window.matchMedia('(min-width: 1000px)').matches;
    return desktop
      ? parseInt(root.getAttribute('data-rug-step-desktop'), 10) || 4
      : parseInt(root.getAttribute('data-rug-step-mobile'), 10) || 2;
  }

  function getInitial(root) {
    var desktop = window.matchMedia('(min-width: 1000px)').matches;
    return desktop
      ? parseInt(root.getAttribute('data-rug-initial-desktop'), 10) || 4
      : parseInt(root.getAttribute('data-rug-initial-mobile'), 10) || 2;
  }

  function applyRevealed(root, revealed) {
    var items = root.querySelectorAll('[data-rug-related-item]');
    items.forEach(function (item, index) { item.hidden = index >= revealed; });

    var moreButton = root.querySelector('[data-rug-related-more]');
    var viewAll = root.querySelector('[data-rug-related-view-all]');
    var exhausted = revealed >= items.length;

    if (moreButton) {
      var wrap = moreButton.closest('.rug-related__actions');
      if (wrap) wrap.hidden = exhausted;
      moreButton.setAttribute('aria-expanded', exhausted ? 'true' : 'false');
    }
    if (viewAll) viewAll.hidden = !exhausted;
    root.dataset.rugRevealed = String(revealed);
  }

  function initRelated(root) {
    if (root.dataset.rugRelatedInitialized === 'true') return;
    root.dataset.rugRelatedInitialized = 'true';
    applyRevealed(root, getInitial(root));
  }

  function revealMoreRelated(moreButton) {
    var root = moreButton.closest('[data-rug-related]');
    if (!root) return;
    var current = parseInt(root.dataset.rugRevealed, 10) || getInitial(root);
    applyRevealed(root, current + getStep(root));
    if (root.querySelector('[data-rug-related-more]')) moreButton.focus();
  }

  function initialize(scope) {
    (scope || document).querySelectorAll('[data-rug-related]').forEach(initRelated);
    runTrustedReviewChecks(scope || document);
    initLinkedVariants(scope || document);
    updateLegacyProductDetails(scope || document);
  }

  initialize(document);

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-rug-related]').forEach(function (root) {
      root.dataset.rugRelatedInitialized = 'false';
      initRelated(root);
    });
    runTrustedReviewChecks(event.target);
    initLinkedVariants(event.target);
    updateLegacyProductDetails(event.target);
  });
})();
